import { Component, Injector, OnDestroy, ViewEncapsulation } from '@angular/core';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { GenerateReportRequest } from '@lsq-frontend/remote-services/enterprise/common';
import {
    GeneratePurchaseReportService,
    GetPurchaseInvoicesService,
    GetPurchasesRequest,
    GetPurchasesResponse,
    GetPurchasesService,
    Purchase
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { forLifeOf, LifecycleAware } from '@lsq-frontend/common-ui';
import { ResponsiveService, Serializer } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { of } from 'rxjs';
import { AnalyticsValue } from '../../../constants/analytics.constants';
import { DateRangeReportComponent } from '../base/date-range-report.component';
import { ReportsUserStateTypes } from '../reports.user-state-type';

@LifecycleAware()
@Component({
    selector: 'purchases-report',
    template: `
        <div class="min-page-width-medium-up">
            <lsq-banner
                [titleText]="title"
                [subTitle]="(dataSource.data$ | async)?.lastUpdatedDate | lastUpdatedDate"
                [hasContent]="true"
                [reportName]="title"
                [analyticsName]="analyticsName"
                [service]="generatePurchaseReportService"
                [hideExport]="dataSource.noData$ | async"
                [hideDatePicker]="!dataSource"
                [datePickerConfiguration]="datePickerConfiguration"
            >
            </lsq-banner>
            <div id="purchases-report-page-container" class="container-fluid page-container">
                <div class="table" *ngIf="dataSource">
                    <purchases-grouped-table
                        [dataSource]="dataSource"
                        [groupBy]="groupPurchasesBy"
                        (requestChange)="dataSource.dispatchRequest($event)"
                    >
                    </purchases-grouped-table>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./purchases.report.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class PurchasesReportComponent extends DateRangeReportComponent implements OnDestroy {
    public title = this._i18n({
        value: 'Purchases',
        meaning: 'Purchases report title'
    });
    public groupPurchasesBy = 'purchaseDate';
    public analyticsName = AnalyticsValue.Purchases;
    public userStateType = ReportsUserStateTypes.GetPurchasesRequest;
    public savedDateFilterName = null;

    public dataSource = new LsqApiServiceListSource<
        GetPurchasesResponse,
        GetPurchasesRequest,
        Purchase[]
    >(this.apiService);

    constructor(
        private _userService: UserService,
        private _i18n: I18n,
        public apiService: GetPurchasesService,
        public getPurchaseInvoicesService: GetPurchaseInvoicesService,
        public generatePurchaseReportService: GeneratePurchaseReportService,
        public serializer: Serializer,
        public responsiveService: ResponsiveService,
        public injector: Injector
    ) {
        super(injector);

        this.dispatchInitialRequest();

        this.dataSource.data$.pipe(forLifeOf(this)).subscribe((response: GetPurchasesResponse) => {
            this.generatePurchaseReportService.setRequestStream(
                of(
                    new GenerateReportRequest({
                        endpointName: this.generatePurchaseReportService.endpointName,
                        jsonRequest: this.serializer.serialize({
                            ...this.dataSource.request,
                            companyKey: this._userService.getCompanyKey()
                        })
                    })
                )
            );
        });
    }

    public ngOnDestroy(): void {}
}
